/**
 * This file provides columns configured for the Table component. Each column type encapsulates rendering logic and sorting behavior.
 * When you want to define a column, consider the following in order:
 * * 1. Use an existing column type.
 * * 2. If no existing type suits the needs of your table, consider defining a new type, if it will conceivably be reused in the future.
 * * 3. As a last resort, use the CUSTOM column type.
 */
import { type JSX } from 'react';

import { DocumentApi } from 'Api/Document/DocumentApi';
import { Button, Link } from 'Components/Buttons/Buttons';
import { CountLinkButton } from 'Components/Buttons/CountLinkButton';
import { CheckboxWithoutLabel } from 'Components/Checkbox/CheckboxWithoutLabel';
import { CircleIndicatorControlEffectiveness } from 'Components/CircleIndicators/CircleIndicatorControlEffectiveness';
import { CircleIndicatorRiskRating } from 'Components/CircleIndicators/CircleIndicatorRiskRating';
import { UploadedFileAndState } from 'Components/Files/UploadedFileAndState';
import { TagListing } from 'Components/Tags/TagListing/TagListing';
import { Text } from 'Components/Text/Text';
import { controlComparator } from 'Helpers/Compare';
import { controlTextToString, getHumanReadableControlIdFromControl } from 'Helpers/ControlFormatter/ControlFormatter';
import { iso8601ToUsDateShort } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { ellipt } from 'Helpers/StringUtils';
import { getFrameworkGroupControlURL } from 'Helpers/URLBuilder/URLBuilder';
import { compareUsersBySubjectForSorting, getUserNameFromSubject } from 'Helpers/UserUtils';
import { UploadedFile } from 'Models/Files';
import { Effectiveness, OperationalControl } from 'Models/OperationalControls';
import { RiskRating } from 'Models/RiskRating';
import { DisplayableTag } from 'Models/Tags';
import { UserResponse } from 'Models/User';

import { PageVariant } from './Table';
import styles from './Table.module.css';

/** Column types that standardize appearance and behavior across the app. Each type (except CUSTOM) renders and sorts identically wherever it's used. */
export enum ColumnType {
    /**
     * Displays a checkbox.
     * Not sortable.
     */
    CHECKBOX,
    /**
     * Displays an OperationalControl as a link (human-readable ID) with ellipted control text below.
     * Sorted using `controlComparator`.
     */
    CONTROL,
    /**
     * Displays control effectiveness with a colored CircleIndicator and text label.
     * Sorted by effectiveness level (INACTIVE is considered below FAIL).
     */
    CONTROL_EFFECTIVENESS,
    /**
     * Displays content according to developer-defined rules.
     * Sorting may or may not be defined.
     * Do not use this column type unless you are absolutely sure you need to. Before using this type, be sure that:
     * * another column type does not suffice, and
     * * it does not make sense to introduce a new, reusable column type.
     */
    CUSTOM,
    /**
     * Displays a date in MM/DD/YY format.
     * Sorted chronologically.
     */
    DATE,
    /**
     * Displays a decimal, formatted to the number of specified digits after the decimal point.
     * Sorted numerically.
     */
    DECIMAL,
    /**
     * Displays string values corresponding to enum values. (Often, the string value of enum members are used directly.)
     * Requires sorting to be defined by the developer, since an enum may or may not be sortable based on the order and values of its members.
     */
    ENUM,
    /**
     * Displays the name of a single file, which can be clicked to be downloaded, as well as file state icon.
     * Sorted alphabetically by filename.
     */
    FILE,
    /**
     * Displays a count of controls as a button that, when clicked, triggers a callback to display an AssociatedControlsModal.
     * Sorted by count.
     */
    MULTIPLE_CONTROLS,
    /**
     * Displays a count of files as a button that, when clicked, triggers a callback to display an AssociatedDocumentsModal.
     * Sorted by count.
     * NOTE: Renders the text "X Documents", which seems to be tailored toward `FoldersTable`--the one table in the app currently using this column type. Consider either making that text configurable or using "Files" everywhere.
     */
    MULTIPLE_FILES,
    /** Displays a list of historical tags (i.e., `DisplayableTag`s) using the TagListing component.
     * Sorted by count.
     */
    MULTIPLE_HISTORICAL_TAGS,
    /** Displays a list of current tags (.e., tags based off of tag IDs) using the TagListing component.
     * Sorted by count.
     */
    MULTIPLE_TAGS,
    /** Displays a `CountLinkButton` with the text "X users". When clicked, the button should display a `UsersModal`.
     * Sorted by count.
     */
    MULTIPLE_USERS,
    /**
     * Displays a primary string (optionally clickable) with an optional secondary string below it.
     * Sorted alphabetically by the primary string.
     */
    PRIMARY_AND_SECONDARY_STRING,
    /**
     * Displays a risk rating with a colored CircleIndicator and text label.
     * Sorted by rating (INACTIVE is considered below LOW).
     */
    RISK_RATING,
    /**
     * Displays a string, optionally clickable as a link or button.
     * Sorted alphabetically.
     */
    STRING,
    /** Displays a user's name looked up from their ID. Sorted by user name. */
    USER,
}

/**
 * Function type for defining click behavior on text elements within cells.
 * Returns either a navigation action, a callback action, or undefined (no click behavior).
 *
 * @typeParam RowData - The row data type.
 * @returns Navigation config, callback config, or undefined.
 */
type GetOnClickTextAction<RowData> = (data: RowData) =>
    | {
          execute?: never;
          /** URL/route to navigate to on click. A hyperlink will be used. */
          goToLocation: string;
          /** If true, opens in a new tab. */
          isExternalUrl?: boolean;
      }
    | {
          /** Callback function to execute on click. A button will be used. */
          execute: (rowData: RowData) => void;
          goToLocation?: never;
      }
    | undefined;

/** Configuration for a table column, where the properties that can be configured depend on the ColumnType. */
export type ColumnConfig<RowData> =
    | {
          type: ColumnType.CHECKBOX;
          name: string;
          getIsChecked: (data: RowData) => boolean;
          getOnChecked: (data: RowData) => (isChecked: boolean) => void;
      }
    | {
          type: ColumnType.CONTROL;
          name: string;
          getControl: (data: RowData) => OperationalControl;
      }
    | {
          type: ColumnType.CONTROL_EFFECTIVENESS;
          name: string;
          getControlEffectiveness: (data: RowData) => Effectiveness;
      }
    | {
          type: ColumnType.CUSTOM;
          name: string;
          getTableCellContent: (data: RowData) => JSX.Element;
          getValue: (data: RowData) => any;
          /** Sort two rows based on the result of their `getValue`s. If omitted, the column will not be sortable. */
          sortComparator?: (a: any, b: any) => number;
      }
    | {
          type: ColumnType.DATE;
          name: string;
          getDateString: (data: RowData) => string | undefined;
          getIcon?: (data: RowData) => JSX.Element | undefined;
          getOnClickTextAction?: GetOnClickTextAction<RowData>;
      }
    | {
          type: ColumnType.DECIMAL;
          name: string;
          getNumber: (data: RowData) => number | undefined;
          numberOfDigitsAfterDecimalPoint: number;
          /**
           * If defined, will be used to decide the final displayed string. The string given to `decorateFormattedNumber` will be the number formatted with `numberOfDigitsAfterDecimalPoint`. For example, to display a number as a percent, pass in: (formattedNumber: string) => `${formattedNumber}%`
           * If not defined, the number will be displayed directly.
           */
          decorateFormattedNumber?: (formattedNumber: string) => string;
      }
    | {
          type: ColumnType.ENUM;
          name: string;
          getString: (data: RowData) => string | undefined;
          /** Required comparator since an enum may or may not be sortable based on the order and values of its members. */
          sortComparator: (a: RowData, b: RowData) => number;
          getIcon?: (data: RowData) => JSX.Element | undefined;
      }
    | {
          type: ColumnType.FILE;
          name: string;
          documentApi: DocumentApi;
          getFile: (data: RowData) => UploadedFile | undefined;
      }
    | {
          type: ColumnType.MULTIPLE_CONTROLS;
          name: string;
          getControls: (data: RowData) => OperationalControl[];
          onClick: (controls: OperationalControl[]) => void;
      }
    | {
          type: ColumnType.MULTIPLE_FILES;
          name: string;
          getFiles: (data: RowData) => UploadedFile[];
          onClick: (files: UploadedFile[]) => void;
      }
    | {
          type: ColumnType.MULTIPLE_HISTORICAL_TAGS;
          name: string;
          getTags: (data: RowData) => DisplayableTag[];
      }
    | {
          type: ColumnType.MULTIPLE_TAGS;
          name: string;
          getTagIds: (data: RowData) => string[];
          /** Converts tag IDs to displayable tags. Pass in the value from `cachedData`. */
          convertTagIdsToDisplayableTags: (tagIds: string[]) => DisplayableTag[];
      }
    | {
          type: ColumnType.MULTIPLE_USERS;
          name: string;
          getUserIds: (data: RowData) => string[];
          noun: string;
          onClick: (userIds: string[]) => void;
      }
    | {
          type: ColumnType.PRIMARY_AND_SECONDARY_STRING;
          name: string;
          getPrimaryString: (data: RowData) => string | undefined;
          getSecondaryString?: (data: RowData) => string | undefined;
          getOnClickPrimaryTextAction?: GetOnClickTextAction<RowData>;
      }
    | {
          type: ColumnType.RISK_RATING;
          name: string;
          getRiskRating: (data: RowData) => RiskRating | undefined;
      }
    | {
          type: ColumnType.STRING;
          name: string;
          getString: (data: RowData) => string | undefined;
          getOnClickTextAction?: GetOnClickTextAction<RowData>;
          getIcon?: (data: RowData) => JSX.Element | undefined;
      }
    | {
          type: ColumnType.USER;
          name: string;
          getUserId: (data: RowData) => string | undefined;
          /** List of users for name lookup. Pass in the value from `cachedData`. */
          users: UserResponse[];
      };

/**
 * Internal representation of a column used by Table for rendering and sorting.
 * Do NOT use this when defining a table. This is only to be used internally by Table.
 *
 * @typeParam RowData - The shape of each row's data object.
 * @typeParam CellValue - The type of value this column extracts from the row data to display.
 */
export type ColumnDefinition<RowData, CellValue> = {
    /** Used as the header label and for uniquely identifying the column. */
    name: string;
    /** Extracts the cell value from row data. Returns undefined if there is no value. */
    getValue: (a: RowData) => CellValue | undefined;

    /**
     * Comparator for sorting. If undefined, the column is not sortable.
     * Receives both cell values and the full row data for maximum flexibility.
     */
    sortComparator?: (aValue: CellValue, bValue: CellValue, aData: RowData, bData: RowData) => number;
    /** Renders the cell content given the row data, the value for the cell, and the page variant. */
    getTableCellContent: (data: RowData, value: CellValue, pageVariant: PageVariant) => JSX.Element;
};

/**
 * Transforms a ColumnConfig (user-defined representation of a column) into a ColumnDefinition (Table's internal representation of a column).
 * In other words, this is the bridge between the developer-friendly interface of standardized column definitions and the messy details of how to render and sort those columns.
 * Do NOT use this when defining a table. This is only to be used internally by Table.
 *
 * @typeParam RowData - The shape of each row's data object.
 * @param config - The column configuration to transform.
 * @returns A ColumnDefinition ready for use by Table.
 */
export const getColumnDefinitionFromColumnConfig = <RowData,>(config: ColumnConfig<RowData>): ColumnDefinition<RowData, any> => {
    switch (config.type) {
        case ColumnType.CHECKBOX:
            const checkboxColumnDefinition: ColumnDefinition<RowData, boolean> = {
                name: config.name,
                getValue: config.getIsChecked,
                getTableCellContent: (data, isChecked) => <CheckboxWithoutLabel status={isChecked ? 'checked' : 'unchecked'} onToggled={config.getOnChecked(data)} />,
            };
            return checkboxColumnDefinition;

        case ColumnType.CONTROL:
            const controlColumnDefinition: ColumnDefinition<RowData, OperationalControl> = {
                name: config.name,
                getValue: config.getControl,
                getTableCellContent: (data, control, pageVariant) => (
                    <>
                        <Link size="sm" to={getFrameworkGroupControlURL(control.identifier)}>
                            {getHumanReadableControlIdFromControl(control)}
                        </Link>
                        <Text noStyles variant="Text4" color={pageVariant === 'standard' ? 'blue' : 'white'}>
                            {ellipt(controlTextToString(control.metadata.control_text), 250)}
                        </Text>
                    </>
                ),
                sortComparator: controlComparator,
            };
            return controlColumnDefinition;

        case ColumnType.CONTROL_EFFECTIVENESS:
            const controlEffectivenessColumnDefinition: ColumnDefinition<RowData, Effectiveness> = {
                name: config.name,
                getValue: config.getControlEffectiveness,
                getTableCellContent: (data: RowData, value: Effectiveness, pageVariant: PageVariant) => <CircleIndicatorControlEffectiveness effectiveness={value} pageVariant={pageVariant} />,
                sortComparator: (a, b) => a - b,
            };
            return controlEffectivenessColumnDefinition;

        case ColumnType.CUSTOM:
            const customColumnDefinition: ColumnDefinition<RowData, any> = {
                name: config.name,
                getValue: config.getValue,
                getTableCellContent: config.getTableCellContent,
                sortComparator: config.sortComparator,
            };
            return customColumnDefinition;

        case ColumnType.DATE:
            const dateColumnDefinition: ColumnDefinition<RowData, string> = {
                name: config.name,
                getValue: config.getDateString,
                getTableCellContent: (data: RowData, value: string, pageVariant: PageVariant) => {
                    const dateElement = (() => {
                        const dateString = iso8601ToUsDateShort(value);
                        const onClickTextAction = config.getOnClickTextAction?.(data);
                        const execute = onClickTextAction?.execute;
                        const linkLocation = onClickTextAction?.goToLocation;
                        if (linkLocation) {
                            return (
                                <Link size="sm" to={linkLocation}>
                                    {dateString}
                                </Link>
                            );
                        } else if (execute) {
                            return (
                                <Button variant="linkText" size="sm" onClick={() => execute(data)}>
                                    {dateString}
                                </Button>
                            );
                        } else {
                            return (
                                <Text noStyles color={pageVariant === 'standard' ? 'blue' : 'white'}>
                                    {dateString}
                                </Text>
                            );
                        }
                    })();

                    const icon = config.getIcon?.(data);

                    return icon ? (
                        <div className={styles.textAndIconContainer}>
                            {dateElement}
                            {icon}
                        </div>
                    ) : (
                        dateElement
                    );
                },
                sortComparator: (a, b) => (a === b ? 0 : a < b ? -1 : 1),
            };
            return dateColumnDefinition;

        case ColumnType.DECIMAL:
            const decimalColumnDefinition: ColumnDefinition<RowData, number> = {
                name: config.name,
                getValue: config.getNumber,
                getTableCellContent: (data: RowData, value: number, pageVariant: PageVariant) => (
                    <Text noStyles color={pageVariant === 'standard' ? 'blue' : 'white'}>
                        {config.decorateFormattedNumber ? config.decorateFormattedNumber(Number(value).toFixed(config.numberOfDigitsAfterDecimalPoint)) : Number(value).toFixed(config.numberOfDigitsAfterDecimalPoint)}
                    </Text>
                ),
                sortComparator: (a, b) => a - b,
            };
            return decimalColumnDefinition;

        case ColumnType.ENUM:
            const enumColumnDefinition: ColumnDefinition<RowData, string> = {
                name: config.name,
                getValue: config.getString,
                getTableCellContent: (data: RowData, value: string, pageVariant: PageVariant) => {
                    const enumElement = (
                        <Text noStyles color={pageVariant === 'standard' ? 'blue' : 'white'}>
                            {value}
                        </Text>
                    );

                    const icon = config.getIcon?.(data);

                    return icon ? (
                        <div className={styles.textAndIconContainer}>
                            {enumElement}
                            {icon}
                        </div>
                    ) : (
                        enumElement
                    );
                },
                sortComparator: (aValue, bValue, aData, bData) => config.sortComparator(aData, bData),
            };
            return enumColumnDefinition;

        case ColumnType.FILE:
            const fileColumnDefinition: ColumnDefinition<RowData, UploadedFile> = {
                name: config.name,
                getValue: config.getFile,
                getTableCellContent: (data: RowData, value: UploadedFile) => <UploadedFileAndState documentApi={config.documentApi} file={value} onDownloadError={console.log} />,
                sortComparator: (a, b) => a.filename.localeCompare(b.filename),
            };
            return fileColumnDefinition;

        case ColumnType.MULTIPLE_CONTROLS:
            const multipleControlsColumnDefinition: ColumnDefinition<RowData, OperationalControl[]> = {
                name: config.name,
                getValue: config.getControls,
                getTableCellContent: (data: RowData, value: OperationalControl[]) => <CountLinkButton count={value.length} noun="Control" onClick={() => config.onClick(config.getControls(data))} />,
                sortComparator: (a, b) => a.length - b.length,
            };
            return multipleControlsColumnDefinition;

        case ColumnType.MULTIPLE_FILES:
            const multipleFilesColumnDefinition: ColumnDefinition<RowData, UploadedFile[]> = {
                name: config.name,
                getValue: config.getFiles,
                getTableCellContent: (data: RowData) => <Button variant="linkText" size="sm" onClick={() => config.onClick(config.getFiles(data))}>{`${config.getFiles(data).length} ${config.getFiles(data).length === 1 ? 'Document' : 'Documents'}`}</Button>,
                sortComparator: (a, b) => a.length - b.length,
            };
            return multipleFilesColumnDefinition;

        case ColumnType.MULTIPLE_HISTORICAL_TAGS:
            const historicalTagsColumnDefinition: ColumnDefinition<RowData, DisplayableTag[]> = {
                name: config.name,
                getValue: config.getTags,
                getTableCellContent: (data: RowData, value: DisplayableTag[]) => <TagListing tags={value} />,
                sortComparator: (a, b) => a.length - b.length,
            };
            return historicalTagsColumnDefinition;

        case ColumnType.MULTIPLE_TAGS:
            const tagsColumnDefinition: ColumnDefinition<RowData, string[]> = {
                name: config.name,
                getValue: config.getTagIds,
                getTableCellContent: (data: RowData, value: string[]) => <TagListing tags={config.convertTagIdsToDisplayableTags(value)} />,
                sortComparator: (a, b) => a.length - b.length,
            };
            return tagsColumnDefinition;

        case ColumnType.MULTIPLE_USERS:
            const usersColumnDefinition: ColumnDefinition<RowData, string[]> = {
                name: config.name,
                getValue: config.getUserIds,
                getTableCellContent: (data: RowData, value: string[]) => <CountLinkButton count={value.length} noun={config.noun} onClick={() => config.onClick(config.getUserIds(data))} />,
                sortComparator: (a, b) => a.length - b.length,
            };
            return usersColumnDefinition;

        case ColumnType.PRIMARY_AND_SECONDARY_STRING:
            const primaryAndSecondaryStringColumnDefinition: ColumnDefinition<RowData, string> = {
                name: config.name,
                getValue: config.getPrimaryString,
                getTableCellContent: (data: RowData, value: string, pageVariant: PageVariant) => {
                    const primaryElement = (() => {
                        const onClickTextAction = config.getOnClickPrimaryTextAction?.(data);
                        const execute = onClickTextAction?.execute;
                        const linkLocation = onClickTextAction?.goToLocation;
                        const isExternalUrl = onClickTextAction?.goToLocation ? (onClickTextAction.isExternalUrl ?? false) : false;
                        if (linkLocation) {
                            return (
                                <Link openInNewTab={isExternalUrl} size="sm" to={linkLocation}>
                                    {value}
                                </Link>
                            );
                        } else if (execute) {
                            return (
                                <Button variant="linkText" size="sm" onClick={() => execute(data)}>
                                    {value}
                                </Button>
                            );
                        } else {
                            return (
                                <Text noStyles preserveLineBreaks color={pageVariant === 'standard' ? 'blue' : 'white'}>
                                    {value}
                                </Text>
                            );
                        }
                    })();

                    const secondaryString = config.getSecondaryString ? config.getSecondaryString(data) : undefined;
                    const secondaryElement = secondaryString ? (
                        <Text noStyles preserveLineBreaks color={pageVariant === 'standard' ? 'darkGray' : 'lightGray'}>
                            {secondaryString}
                        </Text>
                    ) : undefined;

                    if (secondaryElement) {
                        return (
                            <div className={styles.primaryTextAndSecondaryText}>
                                {primaryElement}
                                {secondaryElement}
                            </div>
                        );
                    } else {
                        return primaryElement;
                    }
                },
                sortComparator: (a, b) => a.localeCompare(b),
            };
            return primaryAndSecondaryStringColumnDefinition;

        case ColumnType.RISK_RATING:
            const riskRatingColumnDefinition: ColumnDefinition<RowData, RiskRating> = {
                name: config.name,
                getValue: config.getRiskRating,
                getTableCellContent: (data: RowData, value: RiskRating, pageVariant: PageVariant) => <CircleIndicatorRiskRating riskRating={value} pageVariant={pageVariant} />,
                sortComparator: (a, b) => a - b,
            };
            return riskRatingColumnDefinition;

        case ColumnType.STRING:
            const stringColumnDefinition: ColumnDefinition<RowData, string> = {
                name: config.name,
                getValue: config.getString,
                getTableCellContent: (data: RowData, value: string, pageVariant: PageVariant) => {
                    const onClickTextAction = config.getOnClickTextAction?.(data);
                    const execute = onClickTextAction?.execute;
                    const linkLocation = onClickTextAction?.goToLocation;
                    const isExternalUrl = onClickTextAction?.goToLocation ? (onClickTextAction.isExternalUrl ?? false) : false;

                    const content: JSX.Element = (() => {
                        if (linkLocation) {
                            return (
                                <Link openInNewTab={isExternalUrl} size="sm" to={linkLocation}>
                                    {value}
                                </Link>
                            );
                        } else if (execute) {
                            return (
                                <Button variant="linkText" size="sm" onClick={() => execute(data)}>
                                    {value}
                                </Button>
                            );
                        } else {
                            return (
                                <Text noStyles preserveLineBreaks color={pageVariant === 'standard' ? 'blue' : 'white'}>
                                    {value}
                                </Text>
                            );
                        }
                    })();

                    const icon = config.getIcon?.(data);

                    return icon ? (
                        <div className={styles.textAndIconContainer}>
                            {icon}
                            {content}
                        </div>
                    ) : (
                        content
                    );
                },
                sortComparator: (a, b) => a.localeCompare(b),
            };
            return stringColumnDefinition;

        case ColumnType.USER:
            const userColumnDefinition: ColumnDefinition<RowData, string> = {
                name: config.name,
                getValue: config.getUserId,
                getTableCellContent: (data: RowData, value: string, pageVariant: PageVariant) => (
                    <Text noStyles color={pageVariant === 'standard' ? 'blue' : 'white'}>
                        {getUserNameFromSubject(value, config.users)}
                    </Text>
                ),
                sortComparator: (a, b) => compareUsersBySubjectForSorting(a, b, config.users),
            };
            return userColumnDefinition;
    }
};
